import React, { useEffect, useRef, useState } from 'react';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  height?: string | number;
  closeOnOverlayClick?: boolean;
}

const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  children,
  height,
  closeOnOverlayClick = true,
}) => {
  const bottomSheetRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === overlayRef.current) {
      onClose();
    }
  };

  // Handle modal click to prevent closing when clicking inside modal
  const handleBottomSheetClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Touch/Mouse event handlers for drag to close
  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    setIsDragging(true);
    setStartY(clientY);
    setCurrentY(clientY);
  };

  const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isDragging) return;

    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    setCurrentY(clientY);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;

    const deltaY = currentY - startY;
    const threshold = 100; // Minimum drag distance to close

    if (deltaY > threshold) {
      onClose();
    }

    setIsDragging(false);
    setStartY(0);
    setCurrentY(0);
  };

  if (!isOpen) return null;

  // Calculate transform based on drag
  const dragTransform = isDragging ? `translateY(${Math.max(0, currentY - startY)}px)` : 'translateY(0)';

  // Get bottom sheet height class
  const getBottomSheetHeightClass = () => {
    return height ? 'idto_digilocker_bottom_sheet_custom' : 'idto_digilocker_bottom_sheet_default';
  };

  return (
    <div
      ref={overlayRef}
      className="idto_digilocker_bottom_sheet_overlay"
      onClick={handleOverlayClick}
    >
      <div
        ref={bottomSheetRef}
        className={`idto_digilocker_bottom_sheet_content ${getBottomSheetHeightClass()}`}
        style={{ height: height || undefined, transform: dragTransform }}
        onClick={handleBottomSheetClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleTouchStart}
        onMouseMove={handleTouchMove}
        onMouseUp={handleTouchEnd}
        onMouseLeave={handleTouchEnd}
      >
        {children}
      </div>
    </div>
  );
};

export default BottomSheet;
